import React, { useMemo, forwardRef } from "react";
import {
  Avatar as UICoreAvatar,
  Tooltip,
  XS,
  LG,
  MD,
  SM,
} from "@remo-co/ui-core";
import classNames from "classnames";
import BadgePreview from "modules/settings/companyBadge/BadgePreview";
import { useI18n } from "i18n";
import { IUser } from "modules/app/types";
import starIcon from "./star.svg";
import { ReactComponent as WarningIcon } from "./warning.inline.svg";
import { ReactComponent as RemoBadge } from "./remo-badge.inline.svg";
import { useStyles } from "./styles";
import { parseInitials } from "./utils";

export interface IAvatarProps {
  alt?: string;
  pictureUrl?: string;
  name?: string;
  isAdmin?: boolean;
  isRemoEmployee?: boolean;
  remoEmployeeBadge?: React.ReactNode;
  hasMicCamIssues?: boolean;
  micAndCamIssueLabel?: string;
  remoEmployeeLabel?: string;
  className?: string;
  style?: React.CSSProperties;
  size?: XS | SM | MD | LG;
  onClick?(): void;
  color?: string;
  fallbackBackgroundColor?: string;
  user?: IUser;
  isCompanyBadgeEdit?: boolean;
  id?: string;
  "data-testid"?: string;
  "data-automationid"?: string;
}

const Avatar = forwardRef<HTMLElement, IAvatarProps>((props, ref) => {
  const {
    isAdmin,
    style,
    isRemoEmployee,
    remoEmployeeBadge,
    hasMicCamIssues,
    micAndCamIssueLabel,
    remoEmployeeLabel,
    user,
    isCompanyBadgeEdit,
    pictureUrl,
    alt,
    name = "",
    className = "",
    size = "",
    onClick,
    id,
    "data-testid": dataTestId,
    "data-automationid": dataAutomationId,
  } = props;
  const classes = useStyles(props);
  const badgeInitials = useMemo(() => parseInitials(name), [name]);
  const { t } = useI18n(["event"]);

  return (
    <span
      ref={ref}
      className={classNames(classes.root, {
        [className]: className,
        [classes[size]]: size,
      })}
      style={style}
      aria-label={name}
      id={id}
      data-testid={dataTestId}
      data-automationid={dataAutomationId}
    >
      <UICoreAvatar
        onClick={onClick}
        alt={alt ?? name}
        src={pictureUrl}
        data-testid="avatar-pic"
        classes={{
          root: classes.avatar,
        }}
      >
        {badgeInitials}
      </UICoreAvatar>
      {isAdmin && (
        <img
          alt={t("event:host")}
          src={starIcon}
          className={classes.admin}
          width={28}
          height={28}
        />
      )}
      {isRemoEmployee && remoEmployeeLabel && (
        <Tooltip title={remoEmployeeLabel}>
          <span className={classes.remoBadge}>
            {remoEmployeeBadge ?? <RemoBadge width="36" height="100%" />}
          </span>
        </Tooltip>
      )}
      {!isRemoEmployee && (
        <span className={classes.badge}>
          <BadgePreview
            user={user}
            size="sm"
            isCompanyBadgeEdit={isCompanyBadgeEdit}
          />
        </span>
      )}
      {hasMicCamIssues && micAndCamIssueLabel && (
        <Tooltip title={micAndCamIssueLabel}>
          <span>
            <WarningIcon className={classes.micCamIssues} />
          </span>
        </Tooltip>
      )}
    </span>
  );
});

export default Avatar;
